import React from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { colors, RenewalCard } from '@insurancemenu/imenu-components-lib';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import CartItemDelegate from 'store/modules/proposal/delegates/cartItemDelegate';
import { CartItemDelegateContext } from 'store/pages/proposal/cartItemContext';
import { useProductLineDelegate } from 'store/pages/proposal/productLineContext';
import { useAddQuotePageStore } from 'store/pages/quote/add-quote/pageStore';
import { useRootStore } from 'store/rootStore';
import { breakpointsValues } from '@insurancemenu/imenu-components-lib/dist/utils';
import { ADD_QUOTE_TEST_IDS } from '../../constants';
import {
  AlternativePlansLabelStyled,
  CartItemRowStyled,
  CartItemsListStyled,
  CartItemStyled,
  CartItemsWrapperStyled,
  RemoveButtonStyled,
} from './styles';

interface AddQuoteCartItemsProps {
  productLineId: ProductLineId;
}

const AddQuoteRenewalCard: React.FC<AddQuoteCartItemsProps> = observer(({ productLineId }) => {
  const { isRenewalEnrollment, getInstalledProductLine } = useAddQuotePageStore();
  const {
    domainStoreV2: { domainHeader },
  } = useRootStore();

  const installedProductLine = toJS(getInstalledProductLine(productLineId));

  if (!isRenewalEnrollment || !installedProductLine?.products.length) {
    return null;
  }

  return (
    <RenewalCard
      imPortfolioId={installedProductLine.imPortfolioId}
      carrierPortfolioName={installedProductLine.carrierPortfolioName}
      products={installedProductLine.products}
      onDownload={(): void => {
        installedProductLine.exhibitUrls.map((exhibit) => window.open(exhibit, '_blank'));
      }}
      carrier={{
        logo: domainHeader.logo,
        name: domainHeader.name,
      }}
      isSelected={false}
      breakpoint={breakpointsValues.lg}
    />
  );
});

const AddQuoteCartItems: React.FC<AddQuoteCartItemsProps> = observer(({ productLineId }) => {
  const { canRemovePlan, removePlan, proposalPageStore, isRenewalProductLine, getCartItemsForProductLine } =
    useAddQuotePageStore();

  const productLineDelegate = useProductLineDelegate();
  const portfolioToCartItems = getCartItemsForProductLine(productLineId);
  const hasCartItems = portfolioToCartItems.length > 0;

  return (
    <CartItemsListStyled>
      <AddQuoteRenewalCard productLineId={productLineId} />
      {hasCartItems && (
        <CartItemsWrapperStyled>
          {isRenewalProductLine(productLineId) && (
            <AlternativePlansLabelStyled>Alternative Plans</AlternativePlansLabelStyled>
          )}
          {portfolioToCartItems.map((item) => (
            <CartItemDelegateContext.Provider
              value={
                new CartItemDelegate({
                  pageStore: proposalPageStore,
                  productLineDelegate,
                  portfolioToCartItem: item,
                })
              }
              key={item.portfolio.id}
            >
              <CartItemRowStyled>
                {canRemovePlan ? (
                  <RemoveButtonStyled
                    type="contained"
                    color="custom"
                    size="large"
                    customColor={colors.red[600]}
                    data-test-id={ADD_QUOTE_TEST_IDS.REMOVE_PLAN_BUTTON}
                    onClick={(): void => {
                      void removePlan(item.portfolio.id);
                    }}
                  >
                    Remove
                  </RemoveButtonStyled>
                ) : null}
                <CartItemStyled />
              </CartItemRowStyled>
            </CartItemDelegateContext.Provider>
          ))}
        </CartItemsWrapperStyled>
      )}
    </CartItemsListStyled>
  );
});

export default AddQuoteCartItems;
